import type { ResultOf } from '@graphql-typed-document-node/core'
import {
  CardOutlineIcon,
  GlassSolidIcon,
  TimerOutlineIcon,
} from '@ikyucom/design-system-icons-react'
import clsx from 'clsx'
import { useCallback } from 'react'
import type { ComponentProps, PropsWithChildren, ReactNode } from 'react'
import { LabelNormal } from '~/components/LabelNormal'
import CourseMenu from '~/components/course/CourseMenu'
import type { CourseSelectionInfo } from '~/components/course/CourseSelectionInfo.frag'
import Image from '~/components/image/Image'
import { PlanRestaurantAllowDiscountOnReservation } from '~/components/plan/PlanRestaurantAllowDiscountOnReservation.frag'
import type { PlanSelectionInfo } from '~/components/plan/PlanSelectionInfo.frag'
import { PlanListPriceFragment } from '~/components/plan/planListPrice.frag'
import { FavoritePlanButton } from '~/features/favorite/plan'
import { mainShowTypes, subShowTypes } from '~/features/guide/GuideRestaurantInfo'
import { GuideRestaurantInfo as SdGuideRestaurantInfo } from '~/features/guide/sd/GuideRestaurantInfo'
import { CancelGuardInfo } from '~/features/info/CancelGuardInfo'
import { CancelPolicyRoot } from '~/features/info/CancelPolicyRoot'
import { RestaurantMainInfo, RestaurantSubInfo } from '~/features/info/RestaurantInfo'
import { SharePlanButton as PcSharePlanButton } from '~/features/share/pc/SharePlanButton'
import { SharePlanButton as SpSharePlanButton } from '~/features/share/sd/SharePlanButton'
import { groupDigits } from '~/lib/base/format'
import {
  MEAL_PERIOD_LABEL,
  MEMBER_LIMIT_TYPE,
  MINIMUM_DISCOUNT_RATE,
  PAYPAY_IMAGE_URL,
} from '~/lib/constants/constants'
import { getFragmentData, graphql } from '~/lib/graphql'
import type { FragmentType } from '~/lib/graphql'
import type { MealPeriod } from '~/lib/graphql/graphql'
import type { DateText, HourMinute, PlanId, RestaurantId } from '~/lib/graphql/scalars'
import { money } from '~/lib/money/money'
import { planName } from '~/lib/plan/name'
import { formattedEndDateTime } from '~/lib/plan/timesale'
import { useIsPC } from '~/lib/ua'
import { useScreenStack, useSendClickEvent } from '~/navigation'
import { css, cx, sva } from '~/styled-system/css'
import { hstack } from '~/styled-system/patterns'
import { Collapsible } from '~/ui/Collapsible'
import { CancelGuardLabelWithInformation } from './CancelGuardLabel'
import ChildMenuList from './ChildMenuList'
import { DiscountNotes, TimesaleSpanNote } from './DiscountNotes'
import OptionMenuList from './OptionMenuList'
import PriceBreakdown from './PriceBreakdown'
import RequestReservation from './RequestReservationLabel'
import { SeatList } from './SeatList'
import { PpgOnlyYidDeletionNote } from './YidDeletionNote'
import { useReserveButton } from './useReserveButton'

const PlanRestaurantNotes = graphql(/* GraphQL */ `
  fragment PlanRestaurantNotes on Restaurant {
    notes
  }
`)

interface PlanPrice {
  bestPrice: number
  priceVaried: boolean
  discountRate: number
  pointRate: number
  fixedPoints: number
}

export type Plan = ResultOf<typeof PlanSelectionInfo> & PlanPrice & { planId: PlanId }

type Props = {
  restaurant: ComponentProps<typeof SdGuideRestaurantInfo>['restaurant'] &
    FragmentType<typeof PlanRestaurantNotes> &
    ComponentProps<typeof Header>['restaurant']
  restaurantId: RestaurantId
  plan: Plan
  course: ResultOf<typeof CourseSelectionInfo>
  visitorsCount?: number
  visitDate?: DateText
  visitTime?: HourMinute
  hasNoInventory?: boolean
}

const styles = sva({
  slots: ['root'],
  base: {
    root: {
      p: '4',
      _pc: {
        p: '6',
      },
    },
  },
})

export function PlanDetail({
  restaurant,
  restaurantId,
  plan,
  course,
  visitorsCount,
  visitDate,
  visitTime,
  hasNoInventory = false,
}: Props) {
  // 元値と売値が同額の場合があるため、元値 > 売値の場合だけ元値を表示したい
  const showListPrice =
    plan.listPrice != null && plan.bestPrice != undefined && plan.listPrice > plan.bestPrice
  const listPriceComponents = getFragmentData(PlanListPriceFragment, plan)

  const { top, dialog, bottom } = useReserveButton(plan.planId, hasNoInventory)

  const isPC = useIsPC()
  const classes = styles()
  return (
    <>
      {dialog}
      <section className={classes.root}>
        <Header
          plan={plan}
          restaurantId={restaurantId}
          restaurant={restaurant}
          showListPrice={showListPrice}
          hasNoInventory={hasNoInventory}
        />
        {top}
        <div className={css({ marginTop: '3' })}>
          <CourseMenu
            images={course.images}
            imageHeight={isPC ? 310 : 175}
            imageWidth={isPC ? 620 : 350}
            menuText={course.menuText}
            showReadMoreAccordion={false}
          />
        </div>
        <h2 className="mt-5 text-lg font-bold">プラン紹介</h2>
        <div
          className={css({
            mt: '2',
            spaceY: '3',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word',
            _pc: {
              spaceY: '4',
              fontSize: 'md',
              p: '4',
              borderRadius: 'md',
              bgColor: 'gray.100',
            },
          })}
        >
          <div className={css({ spaceY: '2' })}>
            <p>{plan.description}</p>
            {isPC && (
              <p className={css({ fontSize: 'sm', color: 'gray.700' })}>
                ※料理、席、オプション等の写真はイメージです。
              </p>
            )}
          </div>
          {showListPrice && (
            <div>
              <PriceBreakdown listPriceComponents={listPriceComponents} />
            </div>
          )}
        </div>
        {isPC && (
          <PcDetail
            plan={plan}
            restaurant={restaurant}
            restaurantId={restaurantId}
            visitorsCount={visitorsCount}
            visitDate={visitDate}
            visitTime={visitTime}
          />
        )}
      </section>
      {!isPC && (
        <SdDetail
          plan={plan}
          restaurant={restaurant}
          restaurantId={restaurantId}
          visitorsCount={visitorsCount}
          visitDate={visitDate}
          visitTime={visitTime}
        />
      )}
      {bottom}
    </>
  )
}

type HeaderProps = {
  restaurantId: RestaurantId
  plan: Plan
  restaurant: ComponentProps<typeof PointText>['restaurant']
  showListPrice: boolean
  hasNoInventory: boolean
}

function Header({ plan, restaurantId, restaurant, showListPrice, hasNoInventory }: HeaderProps) {
  const isPC = useIsPC()
  const SharePlanButton = isPC ? PcSharePlanButton : SpSharePlanButton
  return (
    <>
      <div className={hstack({ justifyContent: 'space-between', mb: '2', gap: '4' })}>
        <div className={hstack({ gap: '1', flexWrap: 'wrap' })}>
          <MealPeriodLabel plan={plan} />
          <FreeDrinkLabel plan={plan} />
          <RequestReservationLabel plan={plan} />
          <PaymentRestrictionLabel plan={plan} />
          <MemberLimitTypeLabel plan={plan} />
          <DiscountLabel plan={plan} />
          <CancelGuardLabel plan={plan} />
        </div>
        <div className={hstack({ gap: '2', flexShrink: '0' })}>
          <SharePlanButton plan={plan} />
          <FavoritePlanButton
            planId={plan.planId}
            restaurantId={restaurantId}
            size={isPC ? 'md' : 'sm'}
          />
        </div>
      </div>
      <h1 className={css({ fontWeight: 'bold', fontSize: 'xl', _pc: { fontSize: '2xl' } })}>
        {planName(plan)}
      </h1>

      {plan.isSeatOnly ? // 席のみのプランの場合は料金（0円）を表示しない
      null : (
        <div className="mt-1 flex flex-col text-right">
          <p
            className={cx(
              'flex justify-end font-bold',
              hasNoInventory ? 'text-neutral-600' : '',
              css({ alignItems: 'baseline', fontSize: { base: '2xl', _pc: '3xl' } })
            )}
          >
            {showListPrice && plan.listPrice != null && (
              <span className="mr-2 text-sm font-normal text-neutral-600 line-through">
                {`${money(plan.listPrice)}円`}
              </span>
            )}
            <span>
              {money(plan.bestPrice)}
              <span className={css({ fontSize: { base: 'md', _pc: 'lg' } })}>
                円{plan.priceVaried && <span>〜</span>}
              </span>
            </span>
          </p>
          <PointText price={plan} restaurant={restaurant} />
          <p className={cx('mt-1', css({ color: 'gray.600', fontSize: 'xs' }))}>
            お一人様 消費税・サービス料込
          </p>
        </div>
      )}

      {plan.discountRate >= MINIMUM_DISCOUNT_RATE && (
        <div className="mt-2 text-right">
          <TimesaleSpanNote
            discountRate={plan.discountRate}
            timeSaleEndDate={formattedEndDateTime(plan.timesale?.reservablePeriod.to)}
          />
        </div>
      )}
    </>
  )
}

function MealPeriodLabel({ plan }: { plan: { mealPeriod: MealPeriod } }) {
  return <LabelNormal text={MEAL_PERIOD_LABEL[plan.mealPeriod]} />
}

function FreeDrinkLabel({ plan }: { plan: Plan }) {
  if (!plan.attributes.includes('FREE_DRINK')) {
    return null
  }
  return (
    <LabelNormal text="飲み放題">
      <GlassSolidIcon width={12} height={12} className="mr-1 text-neutral-500" />
    </LabelNormal>
  )
}

function RequestReservationLabel({ plan }: { plan: Plan }) {
  if (!plan.isRequestReservation) {
    return null
  }
  return <RequestReservation />
}

function PaymentRestrictionLabel({ plan }: { plan: Plan }) {
  if (plan.paymentType !== 'ONLINE_ONLY') {
    return null
  }
  return (
    <LabelNormal text="オンラインカード決済限定">
      <CardOutlineIcon width={12} height={12} className="mr-1 text-neutral-500" />
    </LabelNormal>
  )
}

function MemberLimitTypeLabel({ plan }: { plan: Plan }) {
  if (plan.memberLimitType === 'NO_LIMIT') {
    return null
  }
  return <LabelNormal text={MEMBER_LIMIT_TYPE[plan.memberLimitType]} />
}

function DiscountLabel({ plan }: { plan: Plan }) {
  // 価格に変動があれば割引率に'最大'をつける
  const discountLabelText = `${plan.priceVaried ? '最大' : ''}${plan.discountRate}%OFF`
  if (plan.timesale?.visibleLabel) {
    return (
      <>
        <LabelNormal text="タイムセール">
          <TimerOutlineIcon
            width={12}
            height={12}
            className={clsx('mr-1', css({ color: 'timesale.border' }))}
          />
        </LabelNormal>
        {plan.discountRate !== 0 && <LabelNormal hasDiscountRate={true} text={discountLabelText} />}
      </>
    )
  }
  if (plan.discountRate >= MINIMUM_DISCOUNT_RATE) {
    return <LabelNormal hasDiscountRate={true} text={discountLabelText} />
  }
  return null
}

function CancelGuardLabel({ plan }: { plan: { isCancelGuard: boolean } }) {
  if (!plan.isCancelGuard) {
    return null
  }
  return <CancelGuardLabelWithInformation />
}

type DetailProps = {
  plan: Plan
  restaurant: ComponentProps<typeof SdGuideRestaurantInfo>['restaurant'] &
    FragmentType<typeof PlanRestaurantNotes>
  restaurantId: RestaurantId
  visitorsCount?: number
  visitDate?: DateText
  visitTime?: HourMinute
}

const pcDetailStyles = sva({
  slots: ['root', 'contents', 'title'],
  base: {
    root: {
      spaceY: '5',
      mt: '5', // TODO margin は捨てたいが PlanDetail の構造を見直さないといけない
    },
    contents: {
      spaceY: '2',
    },
    title: {
      fontWeight: 'bold',
      fontSize: 'lg',
    },
  },
})

function PcDetail({
  plan,
  restaurant,
  restaurantId,
  visitorsCount,
  visitDate,
  visitTime,
}: DetailProps) {
  const restaurantInfo = getFragmentData(PlanRestaurantNotes, restaurant)
  const viewType = 'planModal'
  const classes = pcDetailStyles()
  return (
    <section className={classes.root}>
      <div className={classes.contents}>
        <h3 className={classes.title}>座席</h3>
        <SeatList
          restaurantId={restaurantId}
          planId={plan.planId}
          visitorsCount={visitorsCount}
          visitDate={visitDate}
          visitTime={visitTime}
        />
      </div>
      {plan.optionMenus && plan.optionMenus.length > 0 && (
        <div className={classes.contents}>
          <h3 className={classes.title}>オプション</h3>
          <OptionMenuList options={plan.optionMenus} />
        </div>
      )}
      {plan.childMenus && plan.childMenus.length > 0 && (
        <div className={classes.contents}>
          <h3 className={classes.title}>お子様メニュー</h3>
          <ChildMenuList childMenus={plan.childMenus} />
        </div>
      )}
      <PcDetailItem title="キャンセルポリシー">
        <CancelPolicyRoot
          cancelPolicy={plan.cancelPolicy}
          hideHeadline={true}
          hidePriorityPlanNotes={true}
        />
      </PcDetailItem>
      <CancelGuardInfo plan={plan} />
      {(restaurantInfo.notes || plan.notes || plan.discountRate >= MINIMUM_DISCOUNT_RATE) && (
        <PcDetailItem title="注意事項">
          <section className="space-y-2 break-all">
            <PreWrapText text={restaurantInfo.notes} />
            <PreWrapText text={plan.notes} />
            <DiscountNotes
              discountRate={plan.discountRate}
              timeSaleEndDate={formattedEndDateTime(plan.timesale?.reservablePeriod.to)}
            />
            <PpgOnlyYidDeletionNote />
          </section>
        </PcDetailItem>
      )}
      <PcDetailItem title="この店舗について">
        <RestaurantMainInfo
          restaurant={restaurant}
          showTypes={mainShowTypes(viewType)}
          direction="vertical"
        />
        <RestaurantSubInfo
          restaurant={restaurant}
          showTypes={subShowTypes(viewType)}
          direction="vertical"
        />
      </PcDetailItem>
    </section>
  )
}

// TODO 他でも使うようなら ui に昇格させる
function PreWrapText({ text }: { text: string | null | undefined }) {
  if (text === undefined || text === null) {
    return null
  }
  const trimmed = text.trim()
  if (trimmed.length === 0) {
    return null
  }
  return <p className={css({ whiteSpace: 'pre-wrap', lineHeight: 'normal' })}>{text}</p>
}

export function PcDetailItem({ title, children }: PropsWithChildren<{ title: ReactNode }>) {
  return (
    <div className={css({ spaceY: '2' })}>
      <h3 className={css({ fontWeight: 'bold', fontSize: 'lg' })}>{title}</h3>
      <div className={css({ bgColor: 'gray.100', p: '4', borderRadius: 'md' })}>{children}</div>
    </div>
  )
}

function SdDetail({
  plan,
  restaurant,
  restaurantId,
  visitorsCount,
  visitDate,
  visitTime,
}: DetailProps) {
  const restaurantInfo = getFragmentData(PlanRestaurantNotes, restaurant)
  const viewType = 'planModal'

  // ガイド or コース選択ふすま のどちらで開かれたプラン詳細モーダルなのか区別するために、2つ前の state を調べる
  const stack = useScreenStack()
  const pageType = stack.at(-2)?.value === 'GUIDE' ? 'planList' : 'courseSelectFusuma'
  const sendClickEvent = useSendClickEvent()
  const sendIASeatEvent = useCallback(
    (open: boolean) => {
      if (open) {
        sendClickEvent({
          [pageType]: 'seat',
          click_event: `newReservationFlow/${pageType}/seat`,
        })
      }
    },
    [pageType, sendClickEvent]
  )

  return (
    <section className={css({ pb: '6' })}>
      <div className="divide-y divide-neutral-300 border-y border-neutral-300 empty:hidden">
        <DetailItem title="座席" bgColor="neutral-100" onOpenChange={sendIASeatEvent}>
          <SeatList
            restaurantId={restaurantId}
            planId={plan.planId}
            visitorsCount={visitorsCount}
            visitDate={visitDate}
            visitTime={visitTime}
          />
        </DetailItem>
        {plan.optionMenus && plan.optionMenus.length > 0 && (
          <DetailItem title="オプション" bgColor="neutral-100">
            <OptionMenuList options={plan.optionMenus} />
          </DetailItem>
        )}
        {plan.childMenus && plan.childMenus.length > 0 && (
          <DetailItem title="お子様メニュー" bgColor="neutral-100">
            <ChildMenuList childMenus={plan.childMenus} />
          </DetailItem>
        )}
        <DetailItem title="キャンセルポリシー">
          <CancelPolicyRoot
            cancelPolicy={plan.cancelPolicy}
            hideHeadline={true}
            hidePriorityPlanNotes={true}
          />
        </DetailItem>
        <CancelGuardInfo plan={plan} />
        {(restaurantInfo.notes || plan.notes || plan.discountRate >= MINIMUM_DISCOUNT_RATE) && (
          <DetailItem title="注意事項">
            <section className="space-y-2 break-all">
              {restaurantInfo.notes && (
                <p className="whitespace-pre-wrap leading-relaxed">{restaurantInfo.notes}</p>
              )}
              {plan.notes && <p className="whitespace-pre-wrap leading-relaxed">{plan.notes}</p>}
              <DiscountNotes
                discountRate={plan.discountRate}
                timeSaleEndDate={formattedEndDateTime(plan.timesale?.reservablePeriod.to)}
              />
              <PpgOnlyYidDeletionNote />
            </section>
          </DetailItem>
        )}
        <SdGuideRestaurantInfo restaurant={restaurant} viewType={viewType} />
      </div>
    </section>
  )
}

export function DetailItem({
  title,
  children,
  bgColor,
  onOpenChange,
}: { title: string } & Pick<
  ComponentProps<typeof Collapsible>,
  'children' | 'bgColor' | 'onOpenChange'
>) {
  return (
    <Collapsible
      header={
        <div className={css({ display: 'flex', alignItems: 'center' })}>
          <h3 className={css({ fontSize: 'lg', fontWeight: 'bold' })}>{title}</h3>
        </div>
      }
      size="wide"
      color="neutral"
      bgColor={bgColor}
      onOpenChange={onOpenChange}
    >
      {children}
    </Collapsible>
  )
}

type PointTextProps = {
  price: { bestPrice: number; pointRate: number; fixedPoints: number }
  restaurant: FragmentType<typeof PlanRestaurantAllowDiscountOnReservation>
}

const pointTextSize = css({
  fontSize: 'xs',
})

export function FixedPointText(price: { fixedPoints: number }) {
  if (price.fixedPoints === 0) {
    return null
  }

  const textColor = css({ color: 'fixedPoint.text' })
  const textWeight = css({ fontWeight: 'bold' })
  return (
    <div className={hstack({ justify: 'flex-end', gap: '1' })}>
      <Image src={PAYPAY_IMAGE_URL} width={14} height={14} alt="paypay" />
      <span className={clsx(textColor, pointTextSize, textWeight)}>
        <span className={css({ fontSize: 'md' })}>{groupDigits(price.fixedPoints)}</span>円分獲得
      </span>
    </div>
  )
}

export function PointText({ price, restaurant }: PointTextProps) {
  if (price.fixedPoints !== 0) {
    return <FixedPointText fixedPoints={price.fixedPoints} />
  }

  if (price.pointRate === 0) {
    return null
  }

  const { pointAvailable: allowDiscountOnReservation } = getFragmentData(
    PlanRestaurantAllowDiscountOnReservation,
    restaurant
  )

  const textColor =
    price.pointRate >= 10
      ? css({ color: 'not.recommended.ikyu.carrot.dark' })
      : css({ color: 'point.text' })

  return (
    <span className={clsx('text-sm', textColor, pointTextSize)}>
      ポイント{price.pointRate}%{allowDiscountOnReservation ? '利用で' : 'で'}
      <span className="font-bold">
        <span className="text-md">{money(rewardedPoints(price.bestPrice, price.pointRate))}</span>円
        {allowDiscountOnReservation ? '割引' : '分獲得'}
      </span>
    </span>
  )
}

function rewardedPoints(price: number, pointRate: number) {
  // 切り捨て
  return Math.floor(price * (pointRate / 100))
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('rewardedPoints', () => {
    expect(rewardedPoints(5555, 10)).toBe(555)
    expect(rewardedPoints(5555, 0)).toBe(0)
  })
}
